import { RegistrationDistrictTokenResponse } from "@carnegie-learning/data-access-angular-passport";
import { Action, createReducer, on } from "@ngrx/store";
import * as registerActions from "../actions/login-register.action";

// reducer state model
export interface RegisterState {
    authorized: boolean;
    email: string;
    inviteHash: string;
    step: number;
    bannerStatus: boolean;
    bannerMessage: string;
    bannerColor: string;
    progressBar: boolean;
    districtRegistrationPayload: RegistrationDistrictTokenResponse | null;
}

// reducer initial state
export const initialState: RegisterState = {
    authorized: false,
    email: "",
    inviteHash: "",
    step: 0,
    bannerStatus: false,
    bannerMessage: "",
    bannerColor: "primary",
    progressBar: false,
    districtRegistrationPayload: null,
};

const registerReducer = createReducer(
    initialState,
    on(registerActions.registerRegistrationCheckAttempt, (state) => ({
        ...state,
        email: "",
        inviteHash: "",
        authorized: false,
        bannerStatus: true,
        bannerMessage: "Verifying email and invite code, please wait.",
        bannerColor: "primary",
        progressBar: true,
    })),
    on(
        registerActions.registerRegistrationCheckSuccess,
        (state, { email, inviteHash }) => ({
            ...state,
            email: email,
            inviteHash: inviteHash,
            authorized: true,
            bannerStatus: false,
            bannerMessage: "Email and invite code verified, redirecting.",
            bannerColor: "primary",
            progressBar: false,
        })
    ),
    on(registerActions.registerRegistrationCheckFail, (state, { payload }) => {
        let checkFailBannerMessage = payload.error.message;

        if (payload.status === 422) {
            checkFailBannerMessage =
                "Email or invite code is invalid, please try again.";
        } else if (payload.status !== 404) {
            checkFailBannerMessage =
                "Passport is experiencing technical difficulties, " +
                "please try again in 5 minutes. [" +
                payload.status +
                "]";
        }

        return {
            ...state,
            authorized: false,
            bannerStatus: true,
            bannerMessage: checkFailBannerMessage,
            bannerColor: "warn",
            progressBar: false,
        };
    }),
    on(registerActions.registerCancel, (state) => ({
        ...state,
        authorized: false,
        bannerStatus: false,
        bannerMessage: "",
        bannerColor: "primary",
        progressBar: false,
    })),
    on(registerActions.registerStep1, (state) => ({
        ...state,
        step: 1,
    })),
    on(registerActions.registerStep2, (state) => ({
        ...state,
        step: 2,
    })),
    on(
        registerActions.registerFormAttemptStudent,
        registerActions.registerFormAttemptTeacher,
        (state) => ({
            ...state,
            progressBar: true,
        })
    ),
    on(registerActions.registerFormSuccess, (state) => ({
        ...state,
        bannerStatus: false,
        bannerMessage: "",
        bannerColor: "primary",
        progressBar: false,
    })),
    on(registerActions.registerFormFail, (state, { payload }) => {
        const errorPayload = payload;

        let bannerMessage = errorPayload.error.message;

        if (errorPayload.status !== 404) {
            bannerMessage =
                "Passport is experiencing technical difficulties, " +
                "please try again in 5 minutes. [" +
                errorPayload.status +
                "]";
        }

        return {
            ...state,
            bannerStatus: true,
            bannerMessage: bannerMessage,
            bannerColor: "warn",
            progressBar: false,
        };
    }),
    on(registerActions.registerAuthFail, (state) => ({
        ...state,
        bannerStatus: false,
        bannerMessage: "",
        bannerColor: "primary",
    })),
    on(registerActions.registerResetState, (state) => {
        return initialState;
    }),
    on(registerActions.registerMaintenance, (state) => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Passport is currently undergoing scheduled maintenance. We'll be back soon." +
            " Thank you for your patience!",
        bannerColor: "warn",
    })),
    on(registerActions.registerFormAttemptExistingStudent, (state) => ({
        ...state,
        bannerStatus: true,
        bannerMessage:
            "Registering your account to the teacher-class, please wait.",
        bannerColor: "primary",
        progressBar: true,
    })),
    on(registerActions.registerFormSuccessExistingUser, (state) => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Account registered, redirecting to log in.",
        bannerColor: "primary",
        progressBar: true,
    })),
    on(registerActions.registerDistrictNoToken, (state) => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "No invite hash found.",
        bannerColor: "warn",
        progressBar: false,
    })),
    on(registerActions.registerDistrictTokenCheckAttempt, (state) => ({
        ...state,
        bannerStatus: true,
        bannerMessage: "Checking district invite token.",
        bannerColor: "primary",
        progressBar: true,
        authorized: false,
        districtRegistrationPayload: null,
    })),
    on(
        registerActions.registerDistrictTokenCheckSuccess,
        (state, { payload }) => {
            if (payload.schools.length < 1) {
                return {
                    ...state,
                    bannerStatus: true,
                    bannerMessage:
                        "No school(s) associated with district. Contact support if this is incorrect.",
                    bannerColor: "warn",
                    progressBar: false,
                    authorized: false,
                    districtRegistrationPayload: null,
                };
            } else {
                return {
                    ...state,
                    bannerStatus: true,
                    bannerMessage:
                        "Verified token, please complete the form below.",
                    bannerColor: "primary",
                    progressBar: false,
                    authorized: true,
                    districtRegistrationPayload: payload,
                };
            }
        }
    ),

    on(
        registerActions.registerDistrictTokenCheckFail,
        (state, { message }) => ({
            ...state,
            bannerStatus: true,
            bannerMessage: message,
            bannerColor: "warn",
            progressBar: false,
            authorized: false,
            districtRegistrationPayload: null,
        })
    )
);

// exporting the reducer so it can be added to the index file
export function reducer(state: RegisterState | undefined, action: Action) {
    return registerReducer(state, action);
}

export const getAuthorized = (state: RegisterState) => state.authorized;
export const getEmail = (state: RegisterState) => state.email;
export const getInviteHash = (state: RegisterState) => state.inviteHash;
export const getStep = (state: RegisterState) => state.step;
export const getRegisterBannerStatus = (state: RegisterState) =>
    state.bannerStatus;
export const getRegisterBannerMessage = (state: RegisterState) =>
    state.bannerMessage;
export const getRegisterBannerColor = (state: RegisterState) =>
    state.bannerColor;
export const getRegisterProgressBar = (state: RegisterState) =>
    state.progressBar;
export const getDistrictRegistrationPayload = (state: RegisterState) =>
    state.districtRegistrationPayload;
